import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listProjectMembers,
  removeProjectMember,
  searchAssignableUsers,
  setProjectMemberRole,
  type AssignableUser,
} from '../../api/projectMembers';
import { DuplicateMemberError } from './memberErrors';
import { isAlreadyMember } from './memberLabels';
import { previewAssignableUsers } from './mockProjects';
import { ProjectMember, ProjectRole } from './types';

export type MembersStatus = 'loading' | 'ready' | 'error';

type UseProjectMembersOptions = {
  projectId: string;
  isPreviewMode: boolean;
  previewMembers?: ProjectMember[];
  savePreviewMembers: (members: ProjectMember[]) => Promise<void>;
  /**
   * Set to false to skip loading and reloading entirely, e.g. for a dialog
   * that stays mounted (and so keeps calling this hook) while closed, where
   * `projectId` alone can't express "don't fetch yet" without the caller
   * resorting to blanking out a real id. Defaults to true.
   */
  enabled?: boolean;
};

export function useProjectMembers({
  projectId,
  isPreviewMode,
  previewMembers,
  savePreviewMembers,
  enabled = true,
}: UseProjectMembersOptions) {
  const [liveMembers, setLiveMembers] = useState<ProjectMember[]>([]);
  const [status, setStatus] = useState<MembersStatus>(
    isPreviewMode || !enabled ? 'ready' : 'loading',
  );
  const loadCounterRef = useRef(0);
  // The members endpoint returns only user ids, so names learned from the user
  // search are kept to label members once they are listed.
  const directoryRef = useRef(new Map<string, AssignableUser>());

  const members = useMemo(
    () => (isPreviewMode ? previewMembers ?? [] : liveMembers),
    [isPreviewMode, previewMembers, liveMembers],
  );

  const reload = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!enabled || isPreviewMode || !projectId) return;
      const loadId = ++loadCounterRef.current;
      if (!silent) setStatus('loading');
      try {
        const listed = await listProjectMembers(projectId);
        if (loadId !== loadCounterRef.current) return;
        setLiveMembers(
          listed.map(member => {
            const known = directoryRef.current.get(member.id);
            return {
              ...member,
              name: member.name || known?.name || '',
              email: member.email || known?.email || '',
            };
          }),
        );
        setStatus('ready');
      } catch {
        if (loadId !== loadCounterRef.current) return;
        setStatus('error');
      }
    },
    [enabled, isPreviewMode, projectId],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    setLiveMembers([]);
    directoryRef.current.clear();
    setStatus(isPreviewMode ? 'ready' : 'loading');
    void reload();
    return () => {
      loadCounterRef.current += 1;
    };
  }, [enabled, isPreviewMode, reload]);

  const searchUsers = useCallback(
    async (query: string, signal?: AbortSignal): Promise<AssignableUser[]> => {
      const found = isPreviewMode
        ? previewAssignableUsers.filter(user =>
            `${user.name} ${user.email}`
              .toLowerCase()
              .includes(query.trim().toLowerCase()),
          )
        : await searchAssignableUsers(projectId, query, { signal });
      found.forEach(user => directoryRef.current.set(user.id, user));
      return found.filter(user => !isAlreadyMember(members, user));
    },
    [isPreviewMode, members, projectId],
  );

  const assignRole = useCallback(
    async (user: AssignableUser, role: ProjectRole) => {
      if (isPreviewMode) {
        const others = members.filter(member => member.id !== user.id);
        await savePreviewMembers([...others, { ...user, role }]);
        return;
      }
      await setProjectMemberRole(projectId, user.id, role);
      directoryRef.current.set(user.id, user);
      await reload({ silent: true });
    },
    [isPreviewMode, members, projectId, reload, savePreviewMembers],
  );

  const addMember = useCallback(
    async (user: AssignableUser, role: ProjectRole) => {
      if (isAlreadyMember(members, user)) throw new DuplicateMemberError();
      await assignRole(user, role);
    },
    [assignRole, members],
  );

  const removeMember = useCallback(
    async (userId: string) => {
      if (isPreviewMode) {
        await savePreviewMembers(
          members.filter(member => member.id !== userId),
        );
        return;
      }
      await removeProjectMember(projectId, userId);
      await reload({ silent: true });
    },
    [isPreviewMode, members, projectId, reload, savePreviewMembers],
  );

  const transferOwnership = useCallback(
    async (userId: string) => {
      if (isPreviewMode) {
        await savePreviewMembers(
          members.map(member => {
            if (member.id === userId) return { ...member, role: 'Owner' };
            return member.role === 'Owner'
              ? { ...member, role: 'Maintainer' }
              : member;
          }),
        );
        return;
      }
      await setProjectMemberRole(projectId, userId, 'Owner');
      await reload({ silent: true });
    },
    [isPreviewMode, members, projectId, reload, savePreviewMembers],
  );

  return {
    members,
    status,
    reload,
    searchUsers,
    addMember,
    assignRole,
    removeMember,
    transferOwnership,
  };
}
